import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';

import { ConfirmationService, MessageService } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { timeout } from 'rxjs/operators';
import {
  ContratoService,
  ClienteService,
} from '@core/services/domain-services';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { ContratoCliente, Cliente } from '@core/models/models';

@Component({
  selector: 'app-contratos',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DrawerModule,
    InputTextModule,
    InputNumberModule,
    DatePickerModule,
    SelectModule,
    SkeletonModule,

    StatusBadgeComponent,
    EmptyStateComponent,
  ],

  templateUrl: './contratos.component.html',
  styleUrl: './contratos.component.scss',
})
export class ContratosComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly svc = inject(ContratoService);
  private readonly clienteSvc = inject(ClienteService);
  private readonly msg = inject(MessageService);
  private readonly confirm = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);

  contratos: ContratoCliente[] = [];
  clientes: Cliente[] = [];
  loading = false;
  showDialog = false;
  editing: ContratoCliente | null = null;
  form!: FormGroup;

  private readonly mockClientes: Cliente[] = [
    {
      id: 1,
      nome: 'Banco BFA, S.A.',
      nif: '5410003691',
      numTelefone: '923 120 120',
      nomeResponsavel: 'António Cardoso',
    },
    {
      id: 2,
      nome: 'Sonangol EP',
      nif: '5000000001',
      numTelefone: '222 000 000',
      nomeResponsavel: 'Carlos Fernandes',
    },
    {
      id: 3,
      nome: 'Angola LNG, S.A.',
      nif: '5000000002',
      numTelefone: '222 111 000',
      nomeResponsavel: 'Maria Trindade',
    },
    {
      id: 4,
      nome: 'TAAG – Linhas Aéreas',
      nif: '5000000003',
      numTelefone: '923 300 300',
      nomeResponsavel: 'João Baptista',
    },
  ];

  private readonly mockContratos: ContratoCliente[] = [
    {
      id: 1,
      idCliente: 1,
      valorMensal: 850000,
      inicioContrato: '2024-01-01',
      fimContrato: '2024-12-31',
      numPostos: 3,
      cliente: {
        id: 1,
        nome: 'Banco BFA, S.A.',
        nif: '5410003691',
        numTelefone: '923 120 120',
        nomeResponsavel: 'António Cardoso',
      },
    },
    {
      id: 2,
      idCliente: 2,
      valorMensal: 2100000,
      inicioContrato: '2024-03-01',
      fimContrato: '2025-02-28',
      numPostos: 8,
      cliente: {
        id: 2,
        nome: 'Sonangol EP',
        nif: '5000000001',
        numTelefone: '222 000 000',
        nomeResponsavel: 'Carlos Fernandes',
      },
    },
    {
      id: 3,
      idCliente: 3,
      valorMensal: 1450000,
      inicioContrato: '2024-06-01',
      fimContrato: '2025-05-31',
      numPostos: 5,
      cliente: {
        id: 3,
        nome: 'Angola LNG, S.A.',
        nif: '5000000002',
        numTelefone: '222 111 000',
        nomeResponsavel: 'Maria Trindade',
      },
    },
    {
      id: 4,
      idCliente: 4,
      valorMensal: 720000,
      inicioContrato: '2023-09-01',
      fimContrato: '2024-08-31',
      numPostos: 2,
      cliente: {
        id: 4,
        nome: 'TAAG – Linhas Aéreas',
        nif: '5000000003',
        numTelefone: '923 300 300',
        nomeResponsavel: 'João Baptista',
      },
    },
  ];

  ngOnInit(): void {
    this.form = this.fb.group({
      idCliente: [null, Validators.required],
      valorMensal: [null, [Validators.required, Validators.min(1)]],
      dataFaturacao: [null],
      inicioContrato: [null, Validators.required],
      fimContrato: [null, Validators.required],
      numPostos: [1, Validators.min(1)],
    });
    this.load();
    this.loadClientes();
  }

  load(): void {
    this.loading = true;
    this.svc
      .getAll()
      .pipe(timeout(8000), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.contratos = res.data?.length ? res.data : this.mockContratos;
          this.loading = false;
        },
        error: () => {
          this.contratos = this.mockContratos;
          this.loading = false;
        },
      });
  }

  loadClientes(): void {
    this.clienteSvc
      .getAll()
      .pipe(timeout(6000), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.clientes = res.data?.length ? res.data : this.mockClientes;
        },
        error: () => {
          this.clientes = this.mockClientes;
        },
      });
  }

  get selectedCliente(): Cliente | undefined {
    const id = this.form.get('idCliente')?.value;
    return this.clientes.find((c) => c.id === id);
  }

  openNew(): void {
    this.editing = null;
    this.form.reset({ numPostos: 1 });
    this.showDialog = true;
  }

  openEdit(c: ContratoCliente): void {
    this.editing = c;
    this.form.patchValue({
      ...c,
      inicioContrato: c.inicioContrato ? new Date(c.inicioContrato) : null,
      fimContrato: c.fimContrato ? new Date(c.fimContrato) : null,
      dataFaturacao: c.dataFaturacao ? new Date(c.dataFaturacao) : null,
    });
    this.showDialog = true;
  }

  getClienteNome(c: ContratoCliente): string {
    return (
      c.cliente?.nome ??
      this.clientes.find((cl) => cl.id === c.idCliente)?.nome ??
      `Cliente #${c.idCliente}`
    );
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const obs = this.editing?.id
      ? this.svc.update(this.editing.id, this.form.value)
      : this.svc.create(this.form.value);
    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.msg.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Contrato guardado',
          life: 3000,
        });
        this.showDialog = false;
        this.load();
      },
    });
  }

  confirmDelete(c: ContratoCliente): void {
    this.confirm.confirm({
      header: 'Eliminar Contrato',
      message: 'Este contrato será eliminado. Deseja continuar?',
      accept: () => {
        if (c.id)
          this.svc
            .delete(c.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.msg.add({
                  severity: 'success',
                  summary: 'Eliminado',
                  detail: 'Contrato eliminado',
                  life: 3000,
                });
                this.load();
              },
            });
      },
    });
  }

  getEstado(c: ContratoCliente): string {
    const hoje = new Date();
    const fim = new Date(c.fimContrato);
    const dias30 = new Date(hoje);
    dias30.setDate(dias30.getDate() + 30);
    if (fim < hoje) return 'EXPIRADO';
    if (fim < dias30) return 'A VENCER';
    return 'ATIVO';
  }

  formatKz(v?: number): string {
    if (v == null) return '—';
    return (
      new Intl.NumberFormat('pt-AO', { maximumFractionDigits: 0 }).format(v) +
      ' Kz'
    );
  }
}
